import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Search, Phone, Mail, DollarSign, AlertTriangle, CheckCircle, UserMinus } from "lucide-react";
import TeacherLayout from "@/components/TeacherLayout";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import StudentAssignmentDialog from "@/components/StudentAssignmentDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { createApiUrl } from "@/lib/api";

interface ApiStudent {
  student_id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  date_of_birth: string;
  address: string;
  alternative_phone: string | null;
  otp_verified: boolean;
  created_at: string;
  class_info: {
    class_id: string;
    class_name: string;
    subject: string;
    grade: string;
    class_fees: number;
  } | null;
  fee_summary: {
    total_fees: number;
    paid_fees: number;
    pending_fees: number;
    overdue_fees: number;
    total_fee_records: number;
  };
  recent_fees: Array<{
    fee_id: string;
    amount: number;
    status: string;
    due_date: string;
    paid_date: string | null;
    created_at: string;
  }>;
}

interface StudentsResponse {
  total_students: number;
  students_with_pending_fees: number;
  summary: {
    total_pending_fees: number;
    total_paid_fees: number;
    total_fees: number;
  };
  students: ApiStudent[];
}

const StudentManagement = () => {
  const [studentsData, setStudentsData] = useState<StudentsResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [removingStudent, setRemovingStudent] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/teacher/login');
        return;
      }

      const response = await fetch(createApiUrl('/teacher/students'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStudentsData(data);
      } else if (response.status === 401) {
        localStorage.removeItem('teacherData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/teacher/login');
      } else {
        throw new Error('Failed to fetch students');
      }
    } catch (error) {
      console.error('Students fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load students",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveStudent = async (studentId: string, classId: string, studentName: string, className: string) => {
    if (removingStudent) return;
    
    setRemovingStudent(studentId);
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/teacher/login');
        return;
      }

      const response = await fetch(createApiUrl(`/teacher/students/${studentId}/classes/${classId}`), {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        toast({
          title: "Student Removed!",
          description: `${studentName} has been removed from ${className}`
        });

        // Refresh students list
        await fetchStudents();
      } else if (response.status === 401) {
        localStorage.removeItem('teacherData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/teacher/login');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove student from class');
      }
    } catch (error) {
      console.error('Remove student error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove student from class",
        variant: "destructive"
      });
    } finally {
      setRemovingStudent(null);
    }
  };

  const filteredStudents = studentsData?.students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (student.class_info?.class_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <TeacherLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading students...</p>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              Student Management
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">Manage your assigned students and their fees</p>
          </div>
          <StudentAssignmentDialog onStudentAssigned={fetchStudents} />
        </div>

        {/* Summary Cards */}
        {studentsData && (
          <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Students</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{studentsData.total_students}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Fees</CardTitle>
                <AlertTriangle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  ₹{studentsData.summary.total_pending_fees.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  {studentsData.students_with_pending_fees} students
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Collected Fees</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ₹{studentsData.summary.total_paid_fees.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Fees</CardTitle>
                <DollarSign className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  ₹{studentsData.summary.total_fees.toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search students by name, email, or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Students Grid */}
        <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredStudents.map((student) => (
            <Card key={student.student_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 mr-2">
                    <CardTitle className="text-base sm:text-lg truncate">{student.name}</CardTitle>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      {student.class_info?.class_name || "Not Assigned"}
                    </p>
                    {student.class_info && (
                      <p className="text-xs text-gray-500 truncate">
                        {student.class_info.grade} • {student.class_info.subject}
                      </p>
                    )}
                  </div>
                  <Badge className={`text-xs flex-shrink-0 ${student.otp_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {student.otp_verified ? 'Verified' : 'Pending'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{student.email}</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{student.phone}</span>
                </div>
                
                {/* Fee Information */}
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">Fee Status</span>
                    <Badge variant={student.fee_summary.pending_fees > 0 ? 'destructive' : 'default'} className="text-xs">
                      {student.fee_summary.pending_fees > 0 ? 'Pending' : 'Paid'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Total: </span>
                      <span className="font-medium">₹{student.fee_summary.total_fees}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Paid: </span>
                      <span className="font-medium text-green-600">₹{student.fee_summary.paid_fees}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Pending: </span>
                      <span className="font-medium text-orange-600">₹{student.fee_summary.pending_fees}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Records: </span>
                      <span className="font-medium">{student.fee_summary.total_fee_records}</span>
                    </div>
                  </div>
                </div>

                {student.address && (
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Address:</p>
                    <p className="text-xs text-gray-500 line-clamp-2">{student.address}</p>
                  </div>
                )}
                
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-500">
                    Joined: {new Date(student.created_at).toLocaleDateString()}
                  </p>
                  {student.date_of_birth && (
                    <p className="text-xs text-gray-500">
                      DOB: {new Date(student.date_of_birth).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Remove Student Button */}
                {student.class_info && (
                  <div className="pt-2 border-t">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-red-600 border-red-200 hover:bg-red-50 text-xs"
                          disabled={removingStudent === student.student_id}
                        >
                          {removingStudent === student.student_id ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-2" />
                          ) : (
                            <UserMinus className="w-3 h-3 mr-2" />
                          )}
                          {removingStudent === student.student_id ? 'Removing...' : 'Remove from Class'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Student from Class</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove "{student.name}" from "{student.class_info.class_name}"? 
                            This action cannot be undone and will affect their fee records.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleRemoveStudent(
                              student.student_id, 
                              student.class_info!.class_id, 
                              student.name, 
                              student.class_info!.class_name
                            )}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={removingStudent === student.student_id}
                          >
                            {removingStudent === student.student_id ? 'Removing...' : 'Remove Student'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredStudents.length === 0 && (
          <Card>
            <CardContent className="text-center py-8 sm:py-12">
              <Users className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">No students found</h3>
              <p className="text-gray-500 mb-3 sm:mb-4 text-sm sm:text-base px-4">
                {searchTerm ? "Try adjusting your search terms" : "No students have been assigned to you yet"}
              </p>
              <p className="text-xs sm:text-sm text-gray-400">
                Use the "Assign Student" button to assign students to your classes.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </TeacherLayout>
  );
};

export default StudentManagement;
